import React, { useCallback, useMemo } from "react";
import clsx from "clsx";
import { isEmpty } from "lodash";
import { FormProvider, useForm } from "react-hook-form";

import { FormContainer, FormContent, LineDivider } from "components/layout";
import { CopyInfoData, PaymentMethodValues, PaymentTypes } from "types";

import { AchFields } from "./achFields";
import { CopyInfo } from "./copyInfo";
import { CreditCardFields } from "./creditCardFields";
import { FormActions } from "./formActions";
import {
	customerIdFieldName,
	paymentTypeFieldName,
} from "./paymentMethodConstants";
import { PaymentTypeFields } from "./paymentTypeFields";
import { StatusFields } from "./statusFields";
import useSubmitPaymentMethod from "./useSubmitPaymentMethod";


import styles from "./paymentMethod.module.scss";

type PaymentMethodFormProps = {
	defaultValues?: PaymentMethodValues;
	isLoading: boolean;
	entityId: string | number;
	paymentMethodId?: string;
	submitService: Function;
	onSuccess: Function;
	copyInfoData?: CopyInfoData;
	isLoadingCopyInfoData?: boolean;
	isModal?: boolean;
	isApplication?: boolean;
	isBillToAgent?: boolean;
};

export const PaymentMethodForm = ({
	defaultValues,
	isLoading,
	entityId,
	paymentMethodId,
	submitService,
	onSuccess,
	copyInfoData,
	isLoadingCopyInfoData,
	isModal = false,
	isApplication,
	isBillToAgent,
}: PaymentMethodFormProps) => {
	const formMethods = useForm({
		mode: "onBlur",
		reValidateMode: "onChange",
		shouldUnregister: false,
		defaultValues,
	});

	const { handleSubmit, watch } = formMethods;

	const submit = useSubmitPaymentMethod({
		onSuccess,
		submitService,
		paymentMethodId,
	});

	const onSubmit = useCallback(
		async values => {
			submit?.(values, entityId, paymentMethodId);
		},
		[entityId, paymentMethodId, submit],
	);

	const paymentType = watch(paymentTypeFieldName);
	const paymentTypeValue = useMemo(
		() => paymentType?.value,
		[paymentType?.value],
	);

	const isPaymentTypeAch = paymentTypeValue === PaymentTypes.ach.value;
	const isPaymentTypeCreditCard =
		paymentTypeValue === PaymentTypes.creditCard.value;

	const isCreditCardCustomerId = !isEmpty(
		defaultValues?.[customerIdFieldName],
	);
	const isPaymentTypeEmpty = isEmpty(defaultValues?.[paymentTypeFieldName]);

	return (
		<FormProvider {...formMethods}>
			<FormContent className={clsx(isModal && styles.modalFormContent)}>
				<FormContainer
					className={clsx(isModal && styles.modalFormContainer)}
				>
					<form onSubmit={handleSubmit(onSubmit)}>
						{isPaymentTypeEmpty && (
							<PaymentTypeFields isLoading={isLoading} />
						)}
						{isPaymentTypeAch && (
							<>
								{!isEmpty(copyInfoData) && (
									<CopyInfo
										copyInfoData={copyInfoData}
										isLoadingCopyInfoData={
											isLoadingCopyInfoData
										}
										isModal={isModal}
										isApplication={isApplication}
										isBillToAgent={isBillToAgent}
									/>
								)}

								<AchFields
									isLoading={isLoading}
									isModal={isModal}
								/>
							</>
						)}
						{isPaymentTypeCreditCard && (
							<CreditCardFields
								isLoading={isLoading}
								defaultValues={defaultValues}
								isModal={isModal}
							/>
						)}
						{(isPaymentTypeCreditCard || isPaymentTypeAch) &&
							!isModal && (
								<StatusFields
									isDisabled={isCreditCardCustomerId}
								/>
							)}
						{!isModal && <LineDivider />}
						{!isCreditCardCustomerId && (
							<FormActions
								className={clsx(
									isModal && styles.modalFormActions,
								)}
								isLoading={isLoading}
							/>
						)}
					</form>
				</FormContainer>
			</FormContent>
		</FormProvider>
	);
};
